import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from './ui/Card';
import weatherService, { type FarmWeatherOverview } from '../services/weather';
import { requestLocationWithDisclosure } from '../services/locationGate';
import {
  Sun, Cloud, CloudRain, CloudSnow, CloudFog, CloudLightning,
  Wind, Droplets, Umbrella, MapPin, Loader2, Radio, Sunrise,
  ChevronLeft, ChevronRight,
} from 'lucide-react';

const STORAGE_KEY = 'weatherCard.lastFarmId';

function pickIcon(conditions: string) {
  const c = conditions.toLowerCase();
  if (c.includes('thunder')) return CloudLightning;
  if (c.includes('snow')) return CloudSnow;
  if (c.includes('rain') || c.includes('drizzle') || c.includes('shower')) return CloudRain;
  if (c.includes('fog')) return CloudFog;
  if (c.includes('clear')) return Sun;
  return Cloud;
}

function haversineKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6371;
  const dLat = (b.lat - a.lat) * Math.PI / 180;
  const dLng = (b.lng - a.lng) * Math.PI / 180;
  const sinDLat = Math.sin(dLat / 2);
  const sinDLng = Math.sin(dLng / 2);
  const h = sinDLat * sinDLat
    + Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180)
    * sinDLng * sinDLng;
  return 2 * R * Math.asin(Math.sqrt(h));
}

type Status = 'loading' | 'no_farms' | 'permission_denied' | 'unavailable' | 'ready';

export function WeatherCard() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>('loading');
  const [farms, setFarms] = useState<FarmWeatherOverview[]>([]);
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  // Load farm overview, then sort by distance to current GPS if available.
  useEffect(() => {
    let cancelled = false;

    async function load() {
      const overview = await weatherService.getFarmsOverview();
      if (cancelled) return;

      const usable = (overview || []).filter((f) => f.snapshot != null && f.lat != null && f.lng != null);

      if (usable.length === 0) {
        // Fall back to GPS forecast — same as old behaviour when no farms exist.
        const loc = await requestLocationWithDisclosure({
          timeout: 10000,
          maximumAge: 10 * 60 * 1000,
          enableHighAccuracy: false,
        });
        if (cancelled) return;
        if (!loc) {
          setStatus('permission_denied');
          return;
        }
        const hh = new Date().getHours().toString().padStart(2, '0');
        const snap = await weatherService.getForecast(loc.coords.latitude, loc.coords.longitude, `${hh}:00`);
        if (cancelled) return;
        if (snap) {
          setFarms([{
            farm_id: 0, farm_name: 'My location', lat: loc.coords.latitude, lng: loc.coords.longitude,
            station_id: null, station_name: null, source: 'forecast', snapshot: snap,
          }]);
          setStatus('ready');
        } else {
          setStatus('unavailable');
        }
        return;
      }

      // Try to sort by distance from current GPS (silently — no prompt).
      // Note: requestLocationWithDisclosure shows a disclosure modal first time;
      // subsequent calls reuse the cached permission. If declined, fall back to alpha.
      const loc = await requestLocationWithDisclosure({
        timeout: 5000,
        maximumAge: 10 * 60 * 1000,
        enableHighAccuracy: false,
      });
      if (cancelled) return;

      let ordered = usable;
      if (loc) {
        const here = { lat: loc.coords.latitude, lng: loc.coords.longitude };
        ordered = [...usable].sort((a, b) =>
          haversineKm(here, { lat: a.lat!, lng: a.lng! }) -
          haversineKm(here, { lat: b.lat!, lng: b.lng! })
        );
      }

      setFarms(ordered);
      // Restore last-viewed farm if still present
      try {
        const lastId = Number(localStorage.getItem(STORAGE_KEY));
        const found = lastId ? ordered.findIndex((f) => f.farm_id === lastId) : -1;
        setIndex(found >= 0 ? found : 0);
      } catch {
        setIndex(0);
      }
      setStatus('ready');
    }

    load();
    return () => { cancelled = true; };
  }, []);

  // Persist last-viewed farm
  useEffect(() => {
    if (status !== 'ready' || farms.length === 0) return;
    const f = farms[index];
    if (!f || f.farm_id === 0) return;
    try { localStorage.setItem(STORAGE_KEY, String(f.farm_id)); } catch { /* ignore */ }
  }, [index, farms, status]);

  const current = farms[index];
  const multi = farms.length > 1;
  const Icon = useMemo(
    () => current?.snapshot ? pickIcon(current.snapshot.conditions) : Cloud,
    [current],
  );

  function next(e?: React.MouseEvent) {
    e?.stopPropagation();
    setIndex((i) => (i + 1) % farms.length);
  }
  function prev(e?: React.MouseEvent) {
    e?.stopPropagation();
    setIndex((i) => (i - 1 + farms.length) % farms.length);
  }

  function handleTouchStart(e: React.TouchEvent) {
    touchStartX.current = e.touches[0].clientX;
  }
  function handleTouchEnd(e: React.TouchEvent) {
    if (touchStartX.current === null || !multi) return;
    const delta = touchStartX.current - e.changedTouches[0].clientX;
    touchStartX.current = null;
    if (Math.abs(delta) < 40) return;
    if (delta > 0) next();
    else prev();
  }

  function handleCardClick() {
    if (!current) {
      navigate('/weather');
      return;
    }
    if (current.station_id) {
      navigate(`/weather/${current.station_id}`);
    } else if (current.farm_id) {
      navigate(`/weather?farm=${current.farm_id}`);
    } else {
      navigate('/weather');
    }
  }

  // ---------------- Loading / fallback states ----------------
  if (status === 'loading') {
    return (
      <Card className="cursor-pointer">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-lg text-neutral-600">Today's Weather</CardTitle>
          <div className="h-12 w-12 rounded-xl bg-sky-100 flex items-center justify-center">
            <Cloud className="h-6 w-6 text-sky-600" />
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex items-center gap-2 text-sm text-neutral-500 mt-1">
            <Loader2 className="h-4 w-4 animate-spin" />
            Fetching weather…
          </div>
        </CardContent>
      </Card>
    );
  }

  if (status === 'permission_denied' || status === 'unavailable' || status === 'no_farms' || !current) {
    return (
      <Card
        className="hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
        onClick={() => navigate('/weather')}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-lg text-neutral-600">Today's Weather</CardTitle>
          <div className="h-12 w-12 rounded-xl bg-sky-100 flex items-center justify-center">
            <Cloud className="h-6 w-6 text-sky-600" />
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex items-center gap-2 mt-1">
            <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-neutral-100 text-neutral-500 text-sm">
              {status === 'permission_denied' ? <MapPin className="h-4 w-4" /> : <Cloud className="h-4 w-4" />}
              {status === 'permission_denied'
                ? 'Enable location to see weather'
                : 'Weather unavailable'}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ---------------- Ready: snapshot view ----------------
  const o = current.snapshot!;
  const isStation = current.source === 'station';

  return (
    <Card
      className="relative hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer select-none group"
      onClick={handleCardClick}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {multi && (
        <>
          <button
            type="button"
            onClick={prev}
            aria-label="Previous farm"
            className="absolute left-1 top-1/2 -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-white/80 backdrop-blur shadow-sm border border-neutral-200 flex items-center justify-center text-neutral-600 hover:bg-white hover:text-sky-600 opacity-70 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={next}
            aria-label="Next farm"
            className="absolute right-1 top-1/2 -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-white/80 backdrop-blur shadow-sm border border-neutral-200 flex items-center justify-center text-neutral-600 hover:bg-white hover:text-sky-600 opacity-70 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </>
      )}

      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="min-w-0 pr-2">
          <CardTitle className="text-lg text-neutral-600 truncate">{current.farm_name}</CardTitle>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${
              isStation ? 'bg-emerald-50 text-emerald-700' : 'bg-sky-50 text-sky-700'
            }`}>
              {isStation ? <Radio className="h-3 w-3" /> : <Cloud className="h-3 w-3" />}
              {isStation ? 'Station' : 'Forecast'}
            </span>
            {multi && (
              <span className="text-[10px] text-neutral-400">{index + 1} / {farms.length}</span>
            )}
          </div>
        </div>
        <div className="h-12 w-12 shrink-0 rounded-xl bg-sky-100 flex items-center justify-center">
          {isStation ? <Radio className="h-6 w-6 text-sky-600" /> : <Icon className="h-6 w-6 text-sky-600" />}
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        <div className="flex items-baseline gap-2 mt-1">
          <span className="text-3xl font-bold text-sky-700">
            {o.temp_c != null ? `${Math.round(Number(o.temp_c))}°C` : '—'}
          </span>
          <span className="text-sm text-neutral-600 truncate">
            {o.conditions}
          </span>
        </div>

        <div className="flex flex-wrap gap-2 mt-3">
          {o.wind_kph != null && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-sky-50 text-sky-700 text-xs font-semibold">
              <Wind className="h-3.5 w-3.5" />
              {Math.round(Number(o.wind_kph))} km/h {o.wind_dir}
            </div>
          )}
          {o.humidity != null && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold">
              <Droplets className="h-3.5 w-3.5" />
              {Math.round(Number(o.humidity))}%
            </div>
          )}
          {o.precip_mm != null && Number(o.precip_mm) > 0 && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 text-xs font-semibold">
              <Umbrella className="h-3.5 w-3.5" />
              {Number(o.precip_mm).toFixed(1)} mm
            </div>
          )}
          {isStation && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold">
              <Sunrise className="h-3.5 w-3.5" />
              Live
            </div>
          )}
        </div>

        {multi && (
          <div
            className="flex justify-center gap-1.5 mt-3"
            onClick={(e) => e.stopPropagation()}
          >
            {farms.map((_, i) => (
              <button
                key={i}
                onClick={(e) => { e.stopPropagation(); setIndex(i); }}
                className={`h-1.5 rounded-full transition-all duration-200 ${
                  i === index
                    ? 'w-5 bg-sky-500'
                    : 'w-1.5 bg-neutral-300 hover:bg-neutral-400'
                }`}
                aria-label={`Farm ${i + 1}`}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default WeatherCard;
